import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  AlarmClock,
  AlertTriangle,
  Archive,
  ArrowRightLeft,
  ArrowLeft,
  BellRing,
  BotOff,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Clock3,
  FileText,
  FlaskConical,
  Paperclip,
  Pill,
  RefreshCw,
  Sparkles,
  UserRound,
} from 'lucide-react';

import {
  MessageDocumentCard,
  RichMessageContent,
} from '@/components/content/RichMessageContent';
import { Badge, Button, Drawer } from '@/design-system/primitives';
import { ApiError } from '@/lib/api/client';
import {
  useClinicianCheckinDoseHistory,
  useClaimConversation,
  useClinicianAttachmentReply,
  useClinicianConversation,
  useClinicianGuides,
  useClinicianQueue,
  useClinicianReply,
  useClinicianRulesAutomations,
  useClinicianSession,
  useDraft,
  useDraftOutcome,
  useConversationHandoffs,
  useHandoffConversation,
  useHandoffTargets,
  usePatientBrief,
  usePatientAIResponseControl,
  useReleaseConversation,
  useRefreshDraft,
  useSnoozeConversation,
  useTransitionConversation,
  useUpdatePatientAIResponseControl,
} from '@/lib/api/clinicianHooks';
import type {
  ConversationAttachment,
  ConversationMessage,
  DraftResponse,
  DraftVariant,
  HandoffTarget,
  IntakeFormPreview,
  PatientBriefResponse,
  QueueItem,
  ReplyDisposition,
} from '@/types/clinician';
import {
  scopedInboxItems,
  type InboxFilter,
  type InboxScope,
  type InboxSort,
} from './inboxFilters';
import { visibleConversationMessages } from './conversationMessages';
import { clinicianMessageError } from './messageError';
import { clinicianInboxDestination } from './clinicianNavigation';
import { QueueTagBadges } from './QueueTagBadges';
import { CheckinDoseHistoryView } from './CheckinDoseHistory';
import { DoseChangeHolds } from './DoseChangeHolds';
import { CareTeamNotes } from './CareTeamNotes';
import { OrderPharmacyLabel } from './OrderPharmacyLabel';
import { PatientFilesCard } from './PatientFilesCard';
import { appendLearningLink, LearningLinkPicker } from './LearningLinkPicker';
import { GuideAttachmentPicker } from './GuideAttachmentPicker';
import { useOwnerFilter } from './ownerFilter';
import { queuePriorityTags } from './queuePriorityTags';
import {
  SubscriptionPlanList,
  SubscriptionStatusSummary,
  subscriptionSummary,
} from './SubscriptionStatus';

type SeededDraft = { id: string; original: string };
type ComposerBuffer = {
  text: string;
  seeded: SeededDraft | null;
  dismissed: string[];
};
type ComposerBufferStore = Map<string, ComposerBuffer>;

/**
 * One conversation: thread + composer on the left, AI summary and the
 * patient's record block on the right. Opening the screen claims the
 * conversation; a refused claim disables the composer and names the
 * colleague holding it (the backend refuses the reply anyway — the UI just
 * says so before typing, not after).
 *
 * Draft outcomes: "Use draft" seeds the composer and remembers the
 * original; send compares final vs original → accepted / edited, and
 * "Dismiss" records rejected. That data decides whether drafts stay.
 */
export function ConversationScreen() {
  const { conversationId = '' } = useParams();
  // React Router reuses this route component when only the URL parameter
  // changes. Keep unsent text in a per-conversation, in-memory buffer while
  // keying the actual workspace so claim refs and async state never leak
  // from one patient to the next. Nothing is persisted to browser storage.
  const composerBuffers = useRef<ComposerBufferStore>(new Map());

  return (
    <ConversationWorkspace
      key={conversationId}
      conversationId={conversationId}
      composerBuffers={composerBuffers.current}
    />
  );
}

function ConversationWorkspace({
  conversationId,
  composerBuffers,
}: {
  conversationId: string;
  composerBuffers: ComposerBufferStore;
}) {
  const { data, isLoading } = useClinicianConversation(conversationId);
  const { data: session } = useClinicianSession();
  const rules = useClinicianRulesAutomations();
  const [searchParams] = useSearchParams();
  // Same owner scope as the inbox this conversation was opened from.
  const ownerFilter = useOwnerFilter();
  const { data: queueData } = useClinicianQueue(
    true,
    ownerFilter.clinicianId,
    ownerFilter.ready,
  );
  const navigate = useNavigate();

  const claim = useClaimConversation(conversationId);
  const release = useReleaseConversation(conversationId);
  const snooze = useSnoozeConversation(conversationId);
  const transition = useTransitionConversation(conversationId);
  const handoff = useHandoffConversation(conversationId);
  const [heldBy, setHeldBy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [handoffOpen, setHandoffOpen] = useState(false);
  const [handoffNote, setHandoffNote] = useState('');
  // "" hands to the patient's own clinician; otherwise a specific person.
  const [handoffTarget, setHandoffTarget] = useState('');
  const handoffTargets = useHandoffTargets(conversationId, handoffOpen);
  const claimedOnce = useRef(false);
  const claimedByMe = useRef(false);
  const leaving = useRef(false);
  const releaseRef = useRef(release.mutate);
  releaseRef.current = release.mutate;
  const scope: InboxScope = {
    filter:
      (searchParams.get('filter') as InboxFilter | null) ??
      (session?.provider_type === 'nurse' ? 'nursing' : 'prescriber'),
    query: searchParams.get('search') ?? '',
    category: searchParams.get('category') ?? '',
    tags: searchParams.getAll('tag'),
    ageHours: searchParams.get('age') ?? '',
    ownerId: searchParams.get('owner') ?? '',
    sort: (searchParams.get('sort') as InboxSort | null) ?? 'smart',
  };
  const scopedItems = scopedInboxItems(queueData?.items ?? [], scope);
  const navigationItems = (
    scopedItems.some((item) => item.conversation_id === conversationId)
      ? scopedItems
      : (queueData?.items ?? []).filter((item) => item.needs_clinician)
  ).filter(
    (item) =>
      item.conversation_id === conversationId ||
      !item.assigned_to ||
      item.assigned_to === session?.clinician_id,
  );
  const currentIndex = navigationItems.findIndex(
    (item) => item.conversation_id === conversationId,
  );
  const previousItem =
    currentIndex > 0 ? navigationItems[currentIndex - 1] : null;
  const nextItem =
    currentIndex >= 0 && currentIndex < navigationItems.length - 1
      ? navigationItems[currentIndex + 1]
      : null;
  const preservedSearch = searchParams.toString();
  const openConversation = useCallback(
    (id: string, replace = false) =>
      navigate(
        {
          pathname: `/clinician-portal/inbox/${id}`,
          search: preservedSearch,
        },
        { replace },
      ),
    [navigate, preservedSearch],
  );
  const returnToInbox = useCallback(
    (replace = false) =>
      navigate(clinicianInboxDestination(preservedSearch), { replace }),
    [navigate, preservedSearch],
  );
  useEffect(() => {
    if (
      !conversationId ||
      !data ||
      claimedOnce.current ||
      !data.state?.needs_clinician
    )
      return;
    claimedOnce.current = true;
    claim
      .mutateAsync()
      .then((r) => {
        claimedByMe.current = r.claimed;
        if (r.claimed && leaving.current) {
          releaseRef.current();
          return;
        }
        setHeldBy(r.claimed ? null : (r.holder_email ?? 'another clinician'));
      })
      .catch(() => setHeldBy(null)); // 404 = not queued yet; composing still fine
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, data]);
  useEffect(
    () => () => {
      leaving.current = true;
      if (claimedByMe.current) releaseRef.current();
    },
    [conversationId],
  );

  const patientName =
    data?.state?.patient.display_name ||
    data?.state?.patient.email ||
    'Patient';

  const finishAndReturn = () => returnToInbox();
  const finishAndAdvance = () => {
    const next =
      nextItem ??
      navigationItems.find((item) => item.conversation_id !== conversationId);
    if (next) openConversation(next.conversation_id, true);
    else returnToInbox(true);
  };
  const state = data?.state;
  const effectiveAIControl = usePatientAIResponseControl(state?.patient.id);
  const conversationTags = state
    ? queuePriorityTags(
        {
          ...state,
          ai_replies_paused:
            effectiveAIControl.data?.paused ?? state.ai_replies_paused,
        },
        rules.data?.tags ?? [],
      )
    : [];
  const actionPending =
    release.isPending ||
    snooze.isPending ||
    transition.isPending ||
    handoff.isPending;
  const nurseBlockedByPrescriberHandoff = Boolean(
    session?.provider_type === 'nurse' &&
    state?.needs_clinician &&
    state.required_capability === 'prescriber',
  );
  const clinicianCredentialMissing = Boolean(
    session && !session.capabilities.message_patients,
  );

  const resolveWithoutReply = async () => {
    const resolvingSafety = state?.urgency === 3;
    if (
      resolvingSafety &&
      !window.confirm(
        'This is marked as a safety conversation. Confirm you reviewed it and no patient reply is needed.',
      )
    ) {
      return;
    }
    setActionError(null);
    try {
      const result = await transition.mutateAsync({
        state: 'resolved',
        reason: resolvingSafety
          ? 'safety_reviewed_no_reply_needed'
          : 'no_reply_needed',
      });
      if (!result.ok) throw new Error('transition refused');
      claimedByMe.current = false;
      leaving.current = true;
      finishAndAdvance();
    } catch {
      setActionError(
        'Could not resolve this conversation. It remains in the queue.',
      );
    }
  };
  const snoozeUntilTomorrow = async () => {
    const until = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
    setActionError(null);
    try {
      const result = await snooze.mutateAsync(until);
      if (!result.ok) throw new Error('snooze refused');
      claimedByMe.current = false;
      leaving.current = true;
      finishAndAdvance();
    } catch {
      setActionError(
        'Could not snooze this conversation. It remains in the queue.',
      );
    }
  };
  const archiveConversation = async () => {
    setActionError(null);
    try {
      const result = await transition.mutateAsync({
        state: 'closed',
        reason: 'archived_by_clinician',
      });
      if (!result.ok) throw new Error('transition refused');
      leaving.current = true;
      finishAndReturn();
    } catch {
      setActionError('Could not archive this conversation.');
    }
  };
  const releaseAndReturn = () => {
    setActionError(null);
    const shouldRelease = claimedByMe.current;
    // Match navigation through the queue rail: leaving a patient should
    // never be blocked because the claim was already released by a reply,
    // another cleanup, or a stale request. Release is best-effort and the
    // server-side TTL remains the final guard if the network is unavailable.
    claimedByMe.current = false;
    leaving.current = true;
    if (shouldRelease) release.mutate();
    finishAndReturn();
  };
  const submitHandoff = async () => {
    const target =
      session?.provider_type === 'nurse' ? 'prescriber' : 'nursing';
    setActionError(null);
    try {
      await handoff.mutateAsync({
        target_capability: target,
        note: handoffNote.trim() || undefined,
        target_clinician_id: handoffTarget || undefined,
      });
      claimedByMe.current = false;
      setHandoffOpen(false);
      setHandoffNote('');
      setHandoffTarget('');
      if (target === 'prescriber') finishAndAdvance();
    } catch (error) {
      setActionError(
        error instanceof ApiError && error.status === 409
          ? 'No active clinician is available for that handoff.'
          : 'Could not hand off this conversation. It remains with you.',
      );
    }
  };

  return (
    <div className="px-15 md:px-25 py-15 md:py-20 flex flex-col gap-15">
      <header className="flex flex-wrap items-center justify-between gap-10 border-b border-line-subtle pb-15">
        <div className="flex items-center gap-10 min-w-0">
          <button
            type="button"
            onClick={releaseAndReturn}
            disabled={release.isPending}
            className="w-40 h-40 inline-flex items-center justify-center border border-line hover:bg-raised disabled:opacity-50 focus-visible:outline-none focus-visible:shadow-focus"
            aria-label="Back to inbox"
          >
            <ArrowLeft size={16} />
          </button>
          <div className="flex flex-col gap-4 min-w-0">
            <div className="flex flex-wrap items-center gap-6">
              <h1 className="type-heading-md truncate">{patientName}</h1>
              {state?.synthetic && (
                <Badge tone="info" size="sm">
                  <FlaskConical size={10} aria-hidden /> Demo
                </Badge>
              )}
              {state?.urgency === 3 && (
                <Badge tone="danger" size="sm">
                  <AlertTriangle size={10} aria-hidden /> Safety
                </Badge>
              )}
              {state?.sla_breached && (
                <Badge tone="warning" size="sm">
                  <Clock3 size={10} aria-hidden /> Overdue
                </Badge>
              )}
              {state?.category === 'triage_error' && (
                <Badge tone="danger" size="sm">
                  AI triage error
                </Badge>
              )}
              {state?.required_capability === 'prescriber' && (
                <Badge tone="warning" size="sm">
                  Prescriber handoff
                </Badge>
              )}
            </div>
            <span className="type-body-sm text-ink-muted">
              {state?.urgency_reason || 'Clinical message review'}
            </span>
            {conversationTags.length > 0 && (
              <QueueTagBadges
                tags={conversationTags}
                visibleLimit={conversationTags.length}
              />
            )}
            {state?.last_handoff_at && (
              <HandoffSummary
                conversationId={conversationId}
                owner={state.work_owner?.display_name ?? state.work_owner?.email}
                note={state.handoff_note}
                at={state.last_handoff_at}
              />
            )}
          </div>
        </div>

        <div className="flex items-center gap-6">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={!previousItem}
            onClick={() =>
              previousItem && openConversation(previousItem.conversation_id)
            }
            title="Previous conversation in this filtered queue (↑)"
          >
            <ChevronLeft size={13} aria-hidden /> Previous
          </Button>
          <span className="type-label-xs tabular-nums text-ink-muted">
            {currentIndex >= 0 ? currentIndex + 1 : '—'} /{' '}
            {navigationItems.length}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={!nextItem}
            onClick={() =>
              nextItem && openConversation(nextItem.conversation_id)
            }
            title="Next conversation in this filtered queue (↓)"
          >
            Next <ChevronRight size={13} aria-hidden />
          </Button>
        </div>

        {state?.needs_clinician && (
          <div className="flex flex-wrap items-center gap-8">
            {session?.provider_type === 'nurse' &&
              state.required_capability !== 'prescriber' && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={actionPending || Boolean(heldBy)}
                  onClick={() => setHandoffOpen(true)}
                >
                  <ArrowRightLeft size={13} aria-hidden /> Escalate to
                  prescriber
                </Button>
              )}
            {session?.provider_type === 'prescriber' &&
              state.required_capability === 'prescriber' && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={actionPending || Boolean(heldBy)}
                  onClick={() => setHandoffOpen(true)}
                >
                  <ArrowRightLeft size={13} aria-hidden /> Return to nursing
                </Button>
              )}
            {state.urgency < 3 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={actionPending || Boolean(heldBy)}
                onClick={snoozeUntilTomorrow}
              >
                <AlarmClock size={13} aria-hidden /> Snooze 1 day
              </Button>
            )}
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={actionPending || Boolean(heldBy)}
              onClick={resolveWithoutReply}
            >
              <CheckCircle2 size={13} aria-hidden /> Mark resolved
            </Button>
          </div>
        )}
        {state &&
          !state.needs_clinician &&
          (state.workflow_state === 'waiting_patient' ||
            state.workflow_state === 'waiting_internal') && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={actionPending}
              onClick={resolveWithoutReply}
            >
              <CheckCircle2 size={13} aria-hidden /> Mark resolved
            </Button>
          )}
        {state &&
          !state.needs_clinician &&
          state.workflow_state === 'resolved' && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={actionPending}
              onClick={archiveConversation}
            >
              <Archive size={13} aria-hidden /> Archive
            </Button>
          )}
      </header>

      {heldBy && (
        <div className="border border-warning/30 bg-warning-wash text-warning px-12 py-8 type-label-sm">
          {heldBy} is working this conversation — the composer is locked to
          avoid crossed replies.
        </div>
      )}
      {actionError && (
        <div
          className="border border-danger/30 bg-danger-wash text-danger px-12 py-8 type-label-sm"
          role="alert"
        >
          {actionError}
        </div>
      )}

      <div className="grid lg:grid-cols-[minmax(0,1fr)_34rem] xl:grid-cols-[22rem_minmax(0,1fr)_34rem] gap-15 items-start">
        <QueueRail
          currentId={conversationId}
          items={navigationItems}
          search={preservedSearch}
          label={scope.filter}
        />
        <div className="flex flex-col gap-15 min-w-0">
          <Thread messages={data?.messages ?? []} loading={isLoading} />
          <Composer
            conversationId={conversationId}
            bufferStore={composerBuffers}
            disabled={
              Boolean(heldBy) ||
              nurseBlockedByPrescriberHandoff ||
              clinicianCredentialMissing
            }
            disabledMessage={
              heldBy
                ? 'Composer locked — conversation claimed'
                : nurseBlockedByPrescriberHandoff
                  ? 'This question is waiting for prescriber review'
                  : 'Your Healthie authoring credential still needs to be connected'
            }
            allowGuides={!state?.synthetic}
            onFinished={(disposition) => {
              if (disposition === 'in_progress') {
                claimedByMe.current = true;
                leaving.current = false;
                return;
              }
              claimedByMe.current = false;
              leaving.current = true;
              finishAndAdvance();
            }}
          />
        </div>
        <aside className="flex flex-col gap-15 lg:sticky lg:top-20 lg:max-h-[calc(100vh-2.5rem)] lg:overflow-y-auto lg:pr-3">
          <PatientBriefCard
            conversationId={conversationId}
            synthetic={Boolean(state?.synthetic)}
          />
        </aside>
      </div>
      {handoffOpen && (
        <div
          className="fixed inset-0 z-[80] bg-black/45 flex items-center justify-center p-15"
          role="dialog"
          aria-modal="true"
          aria-labelledby="handoff-title"
        >
          <div className="w-full max-w-[42rem] border border-line bg-page p-18 flex flex-col gap-12 shadow-xl">
            <div>
              <h2 id="handoff-title" className="type-heading-md">
                {session?.provider_type === 'nurse'
                  ? 'Escalate to prescriber'
                  : 'Return to nursing'}
              </h2>
              <p className="type-body-sm text-ink-muted mt-4">
                {session?.provider_type === 'nurse'
                  ? 'Use this when the patient needs prescribing judgment. AI will not make this handoff automatically.'
                  : 'The nurse becomes the message owner; you remain the patient’s primary prescriber.'}
              </p>
            </div>
            <HandoffTargetPicker
              loading={handoffTargets.isLoading}
              targets={handoffTargets.data?.targets ?? []}
              value={handoffTarget}
              onChange={setHandoffTarget}
            />
            <label className="flex flex-col gap-5 type-label-sm">
              Context for the next clinician (optional)
              <textarea
                value={handoffNote}
                onChange={(event) => setHandoffNote(event.target.value)}
                rows={4}
                className="border border-line bg-raised px-10 py-8 type-body-sm resize-y"
                placeholder="What decision or follow-up is needed?"
              />
            </label>
            <div className="flex justify-end gap-8">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setHandoffOpen(false)}
                disabled={handoff.isPending}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={submitHandoff}
                loading={handoff.isPending}
              >
                Confirm handoff
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function HandoffTargetPicker({
  loading,
  targets,
  value,
  onChange,
}: {
  loading: boolean;
  targets: HandoffTarget[];
  value: string;
  onChange: (value: string) => void;
}) {
  if (loading) {
    return <p className="type-body-sm text-ink-muted">Loading clinicians…</p>;
  }
  if (targets.length <= 1) return null;
  const own = targets.find((target) => target.is_patients_own);
  return (
    <fieldset className="flex flex-col gap-6">
      <legend className="type-label-sm mb-4">Hand to</legend>
      <label className="flex items-start gap-8 type-body-sm">
        <input
          type="radio"
          name="handoff-target"
          checked={value === ''}
          onChange={() => onChange('')}
          className="mt-3"
        />
        <span>
          {own ? `${own.display_name} (the patient’s own)` : 'The patient’s own clinician'}
        </span>
      </label>
      {targets
        .filter((target) => !target.is_patients_own)
        .map((target) => (
          <label
            key={target.clinician_id}
            className="flex items-start gap-8 type-body-sm"
          >
            <input
              type="radio"
              name="handoff-target"
              checked={value === target.clinician_id}
              onChange={() => onChange(target.clinician_id)}
              className="mt-3"
            />
            <span>
              {target.display_name}
              {target.licensed_in_patient_state === false && (
                <span className="text-warning">
                  {' '}
                  · not licensed in the patient’s state
                </span>
              )}
            </span>
          </label>
        ))}
    </fieldset>
  );
}

function HandoffSummary({
  conversationId,
  owner,
  note,
  at,
}: {
  conversationId: string;
  owner: string | null | undefined;
  note: string | null;
  at: string;
}) {
  const [open, setOpen] = useState(false);
  const history = useConversationHandoffs(open ? conversationId : '');
  return (
    <div className="type-body-sm text-ink-muted flex flex-col gap-3">
      <span>
        Handed to <span className="text-ink">{owner || 'a clinician'}</span>{' '}
        · {new Date(at).toLocaleString()}
        {note && <> — “{note}”</>}{' '}
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="underline type-label-xs"
        >
          {open ? 'Hide history' : 'History'}
        </button>
      </span>
      {open && (
        <ol className="flex flex-col gap-3 border-l border-line pl-10">
          {history.isLoading && <li>Loading…</li>}
          {(history.data ?? []).map((event, index) => (
            <li key={`${event.created_at}-${index}`}>
              {event.created_at
                ? new Date(event.created_at).toLocaleString()
                : ''}{' '}
              · {event.from_name || 'Clinician'} →{' '}
              <span className="text-ink">{event.to_name || 'clinician'}</span>
              {event.note && <> — “{event.note}”</>}
            </li>
          ))}
          {history.data?.length === 0 && <li>No handoffs recorded.</li>}
        </ol>
      )}
    </div>
  );
}

function QueueRail({
  currentId,
  items,
  search,
  label,
}: {
  currentId: string;
  items: QueueItem[];
  search: string;
  label: InboxFilter;
}) {
  return (
    <nav
      className="hidden xl:flex border border-line-subtle bg-raised flex-col max-h-[calc(100vh-11rem)] overflow-y-auto"
      aria-label="Clinical queue"
    >
      <div className="px-12 py-10 border-b border-line-subtle flex items-center justify-between">
        <span className="type-label-sm">{label.replaceAll('_', ' ')}</span>
        <div className="flex items-center gap-8 text-ink-muted">
          <span
            className="type-label-xs tracking-normal"
            title="Use the up and down arrow keys to move through the queue"
            aria-label="Use the up and down arrow keys to move through the queue"
          >
            ↑ ↓
          </span>
          <span className="type-label-xs tabular-nums">{items.length}</span>
        </div>
      </div>
      {items.slice(0, 40).map((item) => (
        <QueueRailRow
          key={item.conversation_id}
          item={item}
          active={item.conversation_id === currentId}
          search={search}
        />
      ))}
    </nav>
  );
}

function QueueRailRow({
  item,
  active,
  search,
}: {
  item: QueueItem;
  active: boolean;
  search: string;
}) {
  const name =
    item.patient.display_name || item.patient.email || 'Unknown patient';
  const wait =
    item.waiting_hours < 1
      ? `${Math.max(1, Math.round(item.waiting_hours * 60))}m`
      : item.waiting_hours < 48
        ? `${Math.floor(item.waiting_hours)}h`
        : `${Math.floor(item.waiting_hours / 24)}d`;
  return (
    <Link
      to={{
        pathname: `/clinician-portal/inbox/${item.conversation_id}`,
        search,
      }}
      aria-current={active ? 'page' : undefined}
      className={`px-12 py-10 border-b border-line-subtle flex flex-col gap-5 hover:bg-sunken focus-visible:outline-none focus-visible:shadow-focus ${active ? 'bg-accent-wash' : ''}`}
    >
      <div className="flex items-center gap-5 min-w-0">
        {item.urgency === 3 && (
          <AlertTriangle
            size={11}
            className="text-danger shrink-0"
            aria-label="Safety"
          />
        )}
        <span className="type-body-sm font-medium truncate">{name}</span>
        <span className="ml-auto type-label-xs tabular-nums text-ink-muted">
          {wait}
        </span>
      </div>
      <p className="type-body-sm text-ink-muted line-clamp-2">
        {item.last_patient_message_preview}
      </p>
    </Link>
  );
}

// ── thread ───────────────────────────────────────────────────────────────────

const AUTHOR_LABEL: Record<ConversationMessage['author_kind'], string> = {
  patient: '',
  clinician: 'Clinician',
  bot: 'AI support',
  staff: 'Care team',
};

function Thread({
  messages,
  loading,
}: {
  messages: ConversationMessage[];
  loading: boolean;
}) {
  const endRef = useRef<HTMLDivElement>(null);
  const visibleMessages = visibleConversationMessages(messages);
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [visibleMessages.length]);

  if (loading) {
    return (
      <div className="p-40 text-center type-label-md text-ink-muted border border-line rounded-lg bg-raised">
        Loading conversation…
      </div>
    );
  }

  return (
    <div className="border border-line rounded-lg bg-raised p-15 flex flex-col gap-10 max-h-[60vh] overflow-y-auto">
      {visibleMessages.length === 0 && (
        <p className="type-label-sm text-ink-muted text-center py-20">
          No messages yet.
        </p>
      )}
      {visibleMessages.map((m) => (
        <div
          key={m.id}
          className={`max-w-[85%] flex flex-col gap-2 ${
            m.author_kind === 'patient' ? 'self-start' : 'self-end items-end'
          }`}
        >
          {m.author_kind !== 'patient' && (
            <span className="text-11 text-ink-muted">
              {m.author_name || AUTHOR_LABEL[m.author_kind]}
            </span>
          )}
          <div
            className={`px-12 py-8 rounded-lg type-body-sm whitespace-pre-wrap ${
              m.author_kind === 'patient'
                ? 'bg-sunken'
                : m.author_kind === 'bot'
                  ? 'bg-sunken border border-dashed border-line'
                  : 'bg-ink text-page'
            }`}
          >
            {m.text && (
              <RichMessageContent
                text={m.text}
                inverse={m.author_kind !== 'patient' && m.author_kind !== 'bot'}
              />
            )}
            <ConversationAttachments attachments={m.attachments ?? []} />
          </div>
          {m.created_at && (
            <span className="text-11 text-ink-muted">
              {new Date(m.created_at).toLocaleString([], {
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
              })}
            </span>
          )}
        </div>
      ))}
      <div ref={endRef} />
    </div>
  );
}

function ConversationAttachments({
  attachments,
}: {
  attachments: ConversationAttachment[];
}) {
  if (!attachments.length) return null;
  return (
    <div className="mt-6 flex flex-col gap-8 min-w-[180px]">
      {attachments.map((attachment, index) => {
        const key = `${attachment.url}-${index}`;
        if (attachment.kind === 'image') {
          return (
            <a key={key} href={attachment.url} target="_blank" rel="noreferrer">
              <img
                src={attachment.url}
                alt={attachment.name || 'Patient attachment'}
                loading="lazy"
                className="block max-w-full max-h-96 object-contain rounded border border-line bg-raised"
              />
            </a>
          );
        }
        if (attachment.kind === 'audio') {
          return (
            <audio
              key={key}
              src={attachment.url}
              controls
              className="max-w-full"
            />
          );
        }
        return (
          <MessageDocumentCard
            key={key}
            href={attachment.url}
            title={attachment.name || 'View document'}
            mime={attachment.mime}
          />
        );
      })}
    </div>
  );
}

// ── composer + reply draft ───────────────────────────────────────────────────

function Composer({
  conversationId,
  bufferStore,
  disabled,
  disabledMessage,
  allowGuides,
  onFinished,
}: {
  conversationId: string;
  bufferStore: ComposerBufferStore;
  disabled: boolean;
  disabledMessage: string;
  allowGuides: boolean;
  onFinished: (disposition: ReplyDisposition) => void;
}) {
  const initialBuffer = bufferStore.get(conversationId);
  const [text, setText] = useState(initialBuffer?.text ?? '');
  const [error, setError] = useState<string | null>(null);
  const [guideId, setGuideId] = useState('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Draft ids the clinician dismissed — hides the card without cache
  // surgery (a regenerate via the button still shows the new draft).
  const [dismissed, setDismissed] = useState<string[]>(
    initialBuffer?.dismissed ?? [],
  );
  const reply = useClinicianReply(conversationId);
  const attachmentReply = useClinicianAttachmentReply(conversationId);
  const guides = useClinicianGuides();
  const patientGuides = (guides.data ?? []).filter(
    (guide) => guide.document_kind === 'guide',
  );
  const coaGuides = (guides.data ?? []).filter(
    (guide) =>
      guide.document_kind === 'coa' && guide.coa_scope === 'finished_product',
  );
  const draft = useDraft(conversationId, 'reply', { auto: true });
  const regenerate = useRefreshDraft(conversationId, 'reply');
  const outcome = useDraftOutcome();
  const [variants, setVariants] = useState<DraftResponse[]>([]);
  const [selectedVariant, setSelectedVariant] = useState<DraftVariant>('best');
  // Which draft seeded the composer, and its original text — decides
  // accepted vs edited at send time.
  const seeded = useRef<SeededDraft | null>(initialBuffer?.seeded ?? null);

  useEffect(() => {
    const incoming = draft.data;
    if (!incoming?.id) return;
    setVariants((current) => {
      const existing = current.findIndex(
        (item) => item.variant === incoming.variant,
      );
      const next =
        existing >= 0
          ? current.map((item, index) => (index === existing ? incoming : item))
          : [...current, incoming];
      return next;
    });
    setSelectedVariant(incoming.variant);
  }, [draft.data]);

  const saveBuffer = (patch: Partial<ComposerBuffer>) => {
    const current = bufferStore.get(conversationId) ?? {
      text: '',
      seeded: null,
      dismissed: [],
    };
    bufferStore.set(conversationId, { ...current, ...patch });
  };

  const send = async (disposition: ReplyDisposition) => {
    const content = text.trim();
    if (!content && !guideId && !attachment) return;
    setError(null);
    try {
      if (attachment) {
        await attachmentReply.mutateAsync({
          file: attachment,
          content,
          disposition,
        });
      } else {
        await reply.mutateAsync({
          content,
          disposition,
          guide_id: guideId || undefined,
        });
      }
      let retiredDraftId: string | null = null;
      if (seeded.current) {
        retiredDraftId = seeded.current.id;
        outcome.mutate({
          draftId: seeded.current.id,
          outcome:
            content === seeded.current.original.trim() ? 'accepted' : 'edited',
          finalText: content,
        });
        // Retire the used draft — it stays in the query cache (same id
        // until the patient writes again), and without this it would
        // resurface the moment the composer empties post-send.
      } else if (draftData?.id) {
        // Sent their own reply while an unused draft card was up — that's
        // a rejection in the data, and the card shouldn't outlive the send.
        retiredDraftId = draftData.id;
        outcome.mutate({ draftId: draftData.id, outcome: 'rejected' });
      }
      const nextDismissed = retiredDraftId
        ? Array.from(new Set([...dismissed, retiredDraftId]))
        : dismissed;
      setDismissed(nextDismissed);
      seeded.current = null;
      setText('');
      setGuideId('');
      setAttachment(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (disposition === 'in_progress') {
        saveBuffer({ text: '', seeded: null, dismissed: nextDismissed });
      } else {
        bufferStore.delete(conversationId);
      }
      onFinished(disposition);
    } catch (e) {
      setError(
        clinicianMessageError(
          e,
          'Send failed — nothing was posted. Try again.',
        ),
      );
    }
  };

  const variantIndex = variants.findIndex(
    (item) => item.variant === selectedVariant,
  );
  const draftData =
    variants.find((item) => item.variant === selectedVariant) ?? draft.data;
  const sending = reply.isPending || attachmentReply.isPending;
  const showDraftCard =
    draftData &&
    draftData.id &&
    !dismissed.includes(draftData.id) &&
    !seeded.current &&
    text.trim() === '';

  return (
    <div className="flex flex-col gap-10">
      {showDraftCard && (
        <div className="border border-line rounded-lg bg-raised p-12 flex flex-col gap-8">
          <div className="flex flex-wrap items-center justify-between gap-8">
            <span className="flex items-center gap-6 type-label-sm text-ink-muted">
              <Sparkles size={13} /> Suggested reply — review before sending
            </span>
            <div className="flex items-center gap-5">
              <button
                type="button"
                aria-label="Previous generated reply"
                disabled={variantIndex <= 0}
                onClick={() =>
                  setSelectedVariant(
                    variants[Math.max(0, variantIndex - 1)].variant,
                  )
                }
                className="p-4 border border-line rounded disabled:opacity-30 hover:bg-sunken"
              >
                <ChevronLeft size={12} />
              </button>
              <span className="type-label-xs text-ink-muted tabular-nums">
                {variantIndex + 1} / {Math.max(1, variants.length)} ·{' '}
                {draftData.variant}
              </span>
              <button
                type="button"
                aria-label="Next generated reply"
                disabled={variantIndex >= variants.length - 1}
                onClick={() =>
                  setSelectedVariant(
                    variants[Math.min(variants.length - 1, variantIndex + 1)]
                      .variant,
                  )
                }
                className="p-4 border border-line rounded disabled:opacity-30 hover:bg-sunken"
              >
                <ChevronRight size={12} />
              </button>
            </div>
          </div>
          <p className="type-body-sm whitespace-pre-wrap">
            {draftData.content}
          </p>
          <div className="flex flex-wrap items-center gap-8">
            <button
              type="button"
              className="px-12 py-6 type-label-sm bg-ink text-page rounded hover:opacity-90"
              onClick={() => {
                seeded.current = {
                  id: draftData.id!,
                  original: draftData.content,
                };
                setText(draftData.content);
                saveBuffer({
                  text: draftData.content,
                  seeded: seeded.current,
                });
              }}
            >
              Use draft
            </button>
            <button
              type="button"
              className="px-12 py-6 type-label-sm border border-line rounded hover:bg-sunken"
              onClick={() => {
                outcome.mutate({ draftId: draftData.id!, outcome: 'rejected' });
                const next = Array.from(new Set([...dismissed, draftData.id!]));
                setDismissed(next);
                saveBuffer({ dismissed: next });
              }}
            >
              Dismiss
            </button>
            <span className="ml-auto type-label-xs text-ink-muted">
              Another version:
            </span>
            {(
              [
                ['best', 'Best'],
                ['shorter', 'Shorter'],
                ['warmer', 'Warmer'],
                ['clarifying', 'Ask first'],
              ] as [DraftVariant, string][]
            ).map(([variant, label]) => (
              <button
                key={variant}
                type="button"
                disabled={regenerate.isPending}
                onClick={() => regenerate.mutate(variant)}
                className="px-8 py-5 type-label-xs border border-line rounded hover:bg-sunken disabled:opacity-40"
              >
                {label}
              </button>
            ))}
          </div>
          {regenerate.isPending && (
            <p className="text-11 text-ink-muted">Drafting another option…</p>
          )}
        </div>
      )}

      <div className="border border-line rounded-lg bg-raised p-12 flex flex-col gap-8">
        <div className="flex flex-wrap items-center gap-8">
          {allowGuides && (
            <>
              <GuideAttachmentPicker
                guides={[...patientGuides, ...coaGuides]}
                value={guideId}
                disabled={disabled || sending}
                loading={guides.isLoading}
                failed={guides.isError}
                onRetry={() => void guides.refetch()}
                onChange={(id) => {
                  setGuideId(id);
                  if (id) {
                    setAttachment(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }
                }}
              />
              <LearningLinkPicker
                disabled={disabled || sending}
                onSelect={(title, url) => {
                  const nextText = appendLearningLink(text, title, url);
                  setText(nextText);
                  saveBuffer({ text: nextText, seeded: seeded.current });
                }}
              />
            </>
          )}
        </div>
        {allowGuides && (
          <div className="flex flex-wrap items-center gap-8">
            <label className="inline-flex cursor-pointer items-center gap-6 border border-line rounded px-9 py-6 type-label-xs hover:bg-sunken focus-within:shadow-focus">
              <Paperclip size={13} aria-hidden />
              Attach image or PDF
              <input
                ref={fileInputRef}
                type="file"
                className="sr-only"
                accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                disabled={disabled || sending}
                onChange={(event) => {
                  const selected = event.target.files?.[0] ?? null;
                  if (selected && selected.size > 10 * 1024 * 1024) {
                    setError('Attachment must be 10 MB or smaller.');
                    event.target.value = '';
                    return;
                  }
                  setError(null);
                  setAttachment(selected);
                  if (selected) setGuideId('');
                }}
              />
            </label>
            {attachment && (
              <span className="inline-flex min-w-0 items-center gap-6 bg-sunken rounded px-8 py-5 type-body-sm">
                <span className="max-w-[18rem] truncate">
                  {attachment.name}
                </span>
                <span className="text-ink-muted">
                  {(attachment.size / 1024 / 1024).toFixed(1)} MB
                </span>
                <button
                  type="button"
                  className="type-label-xs text-danger hover:underline"
                  onClick={() => {
                    setAttachment(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                >
                  Remove
                </button>
              </span>
            )}
          </div>
        )}
        <textarea
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            saveBuffer({ text: e.target.value, seeded: seeded.current });
          }}
          placeholder={disabled ? disabledMessage : 'Reply to the patient…'}
          disabled={disabled || sending}
          rows={4}
          className="w-full bg-sunken border border-line rounded px-10 py-8 type-body-sm outline-none resize-y disabled:opacity-50"
        />
        <div className="flex items-center gap-8">
          <Button
            type="button"
            size="sm"
            onClick={() => send('resolved')}
            disabled={
              disabled || sending || (!text.trim() && !guideId && !attachment)
            }
          >
            {sending ? 'Sending…' : 'Send & resolve'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => send('waiting_patient')}
            disabled={
              disabled || sending || (!text.trim() && !guideId && !attachment)
            }
          >
            Send & wait for patient
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => send('in_progress')}
            disabled={
              disabled || sending || (!text.trim() && !guideId && !attachment)
            }
          >
            Send & keep open
          </Button>
          {error && <span className="type-label-sm text-danger">{error}</span>}
        </div>
        <p className="text-11 text-ink-muted">
          Keep open when you still owe follow-up. Wait when the patient owes the
          next response. Resolve only when the clinical question is done. Guide
          Library guides are sent as secure patient-view links. Custom images
          and PDFs are posted directly into the Healthie conversation and kept
          in the patient attachment history.
        </p>
      </div>
    </div>
  );
}

// ── sidebar ──────────────────────────────────────────────────────────────────
//
// Content in these cards uses type-body-* — the label styles uppercase in
// this design system, which turned the whole sidebar into a wall of caps
// (QA screenshot 2026-08-04). Labels are for chrome, body is for reading.

/**
 * Renders the summary's markdown-lite: `**Section** — text` lines become
 * headed paragraphs, `- ` lines become bullets, everything else is prose.
 */
function SummaryText({ text }: { text: string }) {
  const blocks = text
    .split('\n')
    .filter((l) => l.trim() !== '' && l.trim() !== '**');
  return (
    <div className="flex flex-col gap-6">
      {blocks.map((line, i) => {
        const heading = line.match(/^\*\*(.+?)\*\*\s*(?:—|-)?\s*(.*)$/);
        if (heading) {
          return (
            // eslint-disable-next-line react/no-array-index-key
            <p key={i} className="type-body-sm leading-relaxed">
              <span className="font-semibold">{heading[1]}</span>
              {heading[2] ? <> — {heading[2].replace(/\*\*/g, '')}</> : null}
            </p>
          );
        }
        if (line.trim().startsWith('- ')) {
          return (
            // eslint-disable-next-line react/no-array-index-key
            <p key={i} className="type-body-sm leading-relaxed pl-10 relative">
              <span className="absolute left-2">•</span>
              {line.trim().slice(2).replace(/\*\*/g, '')}
            </p>
          );
        }
        return (
          // eslint-disable-next-line react/no-array-index-key
          <p key={i} className="type-body-sm leading-relaxed">
            {line.replace(/\*\*/g, '')}
          </p>
        );
      })}
    </div>
  );
}

function SummaryCard({ conversationId }: { conversationId: string }) {
  const summary = useDraft(conversationId, 'summary', { auto: true });
  const refresh = useRefreshDraft(conversationId, 'summary');
  const busy = summary.isFetching || refresh.isPending;
  return (
    <section className="p-12 flex flex-col gap-8 min-w-0">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-6 type-label-sm font-medium">
          <Sparkles size={13} /> AI summary
        </span>
        <button
          type="button"
          onClick={() => refresh.mutate('best')}
          disabled={busy}
          aria-label="Refresh summary"
          className="p-4 rounded hover:bg-sunken disabled:opacity-40"
        >
          <RefreshCw size={13} className={busy ? 'animate-spin' : ''} />
        </button>
      </div>
      {summary.isLoading ? (
        <p className="type-body-sm text-ink-muted">Summarising…</p>
      ) : summary.isError || refresh.isError ? (
        <p className="type-body-sm text-ink-muted">Summary unavailable.</p>
      ) : !summary.data?.content.trim() ? (
        <p className="type-body-sm text-ink-muted">
          No summary was generated. Use refresh to try again.
        </p>
      ) : (
        <SummaryText text={summary.data?.content ?? ''} />
      )}
    </section>
  );
}

const STATUS_TONE: Record<string, string> = {
  delivered: 'text-success',
  shipped: 'text-success',
  cancelled: 'text-ink-muted line-through',
  failed: 'text-danger',
};

function AIResponseControl({ patientId }: { patientId: string }) {
  const control = usePatientAIResponseControl(patientId);
  const update = useUpdatePatientAIResponseControl(patientId);
  const [editing, setEditing] = useState(false);
  const [duration, setDuration] = useState<
    '24h' | '48h' | 'custom' | 'indefinite'
  >('24h');
  const [customValue, setCustomValue] = useState(24);
  const [customUnit, setCustomUnit] = useState<'hours' | 'days'>('hours');
  const [error, setError] = useState<string | null>(null);

  const savePause = async () => {
    setError(null);
    let mode: 'until' | 'indefinite' = 'until';
    let pausedUntil: string | undefined;
    if (duration === 'indefinite') {
      mode = 'indefinite';
    } else {
      const amount =
        duration === '24h' ? 24 : duration === '48h' ? 48 : customValue;
      const hours =
        duration === 'custom' && customUnit === 'days' ? amount * 24 : amount;
      if (!Number.isFinite(hours) || hours <= 0) {
        setError('Enter a duration greater than zero.');
        return;
      }
      pausedUntil = new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();
    }
    try {
      await update.mutateAsync({
        mode,
        paused_until: pausedUntil,
        reason: 'Clinician taking over patient messages',
      });
      setEditing(false);
    } catch {
      setError('AI reply settings could not be updated. Try again.');
    }
  };

  const resume = async () => {
    setError(null);
    try {
      await update.mutateAsync({
        mode: 'enabled',
        reason: 'Clinician resumed automated replies',
      });
      setEditing(false);
    } catch {
      setError('AI reply settings could not be updated. Try again.');
    }
  };

  if (control.isLoading) {
    return <p className="text-11 text-ink-muted">Checking AI reply status…</p>;
  }
  if (control.isError || !control.data) {
    return (
      <p className="text-11 text-danger">
        AI reply status unavailable. Automated replies are suppressed as a
        precaution.
      </p>
    );
  }

  const state = control.data;
  const pauseLabel = state.paused_indefinitely
    ? 'Paused indefinitely'
    : state.paused_until
      ? `Paused until ${new Date(state.paused_until).toLocaleString([], {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })}`
      : 'Paused';

  return (
    <div className="mt-5 border border-line-subtle bg-sunken p-8 flex flex-col gap-7">
      <div className="flex items-center justify-between gap-8">
        <span className="flex items-center gap-6 type-label-xs font-medium">
          {state.paused ? (
            <BotOff size={13} aria-hidden />
          ) : (
            <BellRing size={13} aria-hidden />
          )}
          AI auto-replies
        </span>
        <Badge tone={state.paused ? 'warning' : 'success'} size="sm">
          {state.paused ? pauseLabel : 'On'}
        </Badge>
      </div>
      <p className="text-11 text-ink-muted leading-relaxed">
        {state.paused
          ? "AI will not message this patient. New messages still move into the human queue, use the clinician's configured notifications, and receive AI summaries and drafts."
          : 'AI may answer eligible messages. Clinical and escalated messages still enter the human queue.'}
      </p>
      {state.lookup_failed && (
        <p className="text-11 text-danger">
          Settings could not be verified, so AI replies are off as a precaution.
        </p>
      )}
      {!editing ? (
        <Button
          type="button"
          size="sm"
          variant={state.paused ? 'primary' : 'secondary'}
          onClick={() => (state.paused ? void resume() : setEditing(true))}
          loading={update.isPending}
        >
          {state.paused ? 'Resume AI replies' : 'Pause AI replies'}
        </Button>
      ) : (
        <div className="flex flex-col gap-7">
          <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
            Pause for
            <select
              value={duration}
              onChange={(event) =>
                setDuration(event.target.value as typeof duration)
              }
              className="min-h-34 border border-line bg-raised px-8 type-body-sm text-ink"
            >
              <option value="24h">24 hours</option>
              <option value="48h">48 hours</option>
              <option value="custom">Custom duration</option>
              <option value="indefinite">Indefinitely</option>
            </select>
          </label>
          {duration === 'custom' && (
            <div className="grid grid-cols-[1fr_auto] gap-6">
              <input
                type="number"
                min={1}
                max={365}
                value={customValue}
                onChange={(event) => setCustomValue(Number(event.target.value))}
                aria-label="Custom AI pause duration"
                className="min-w-0 min-h-34 border border-line bg-raised px-8 type-body-sm text-ink"
              />
              <select
                value={customUnit}
                onChange={(event) =>
                  setCustomUnit(event.target.value as typeof customUnit)
                }
                aria-label="Custom AI pause unit"
                className="min-h-34 border border-line bg-raised px-8 type-body-sm text-ink"
              >
                <option value="hours">hours</option>
                <option value="days">days</option>
              </select>
            </div>
          )}
          <div className="flex items-center gap-6">
            <Button
              type="button"
              size="sm"
              onClick={() => void savePause()}
              loading={update.isPending}
            >
              Pause AI replies
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setEditing(false)}
              disabled={update.isPending}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
      {error && <p className="text-11 text-danger">{error}</p>}
    </div>
  );
}

function PatientBriefCard({
  conversationId,
  synthetic,
}: {
  conversationId: string;
  synthetic: boolean;
}) {
  const brief = usePatientBrief(conversationId);
  const [chartOpen, setChartOpen] = useState(false);
  const [chartView, setChartView] = useState<ChartView>(
    'overview',
  );
  const data = brief.data;
  const profile = data?.profile ?? {};
  const medications = data?.medications ?? [];
  const orders = data?.orders ?? [];
  const evidence = data?.intake_evidence ?? [];
  const intakeForms = data?.intake_forms ?? [];
  const relevantIntakeIds = new Set(data?.relevant_intake_form_ids ?? []);
  const productIntakes = intakeForms.filter(
    (form) => form.form_id && relevantIntakeIds.has(form.form_id),
  );
  const appointments = data?.appointments ?? [];

  if (brief.isLoading) {
    return (
      <>
        <div className="border border-line rounded-lg bg-raised p-12">
          <p className="type-body-sm text-ink-muted">Loading patient brief…</p>
        </div>
        <PatientFilesCard
          key={conversationId}
          conversationId={conversationId}
          synthetic={synthetic}
        />
      </>
    );
  }

  if (brief.isError || !data) {
    return (
      <>
        <div className="border border-line rounded-lg bg-raised p-12">
          <p className="type-body-sm text-ink-muted">
            Patient brief unavailable.
          </p>
        </div>
        <PatientFilesCard
          key={conversationId}
          conversationId={conversationId}
          synthetic={synthetic}
        />
      </>
    );
  }

  const identity = [
    profile.age ? `${profile.age} years` : null,
    profile.sex,
    profile.state,
    profile.timezone,
  ].filter(Boolean);
  const openChart = (view: ChartView) => {
    setChartView(view);
    setChartOpen(true);
  };

  return (
    <>
      <div
        id="patient-clinical-context"
        className="border border-line rounded-lg bg-raised flex flex-col divide-y divide-line-subtle scroll-mt-20"
      >
        <section className="p-12 flex flex-col gap-6 min-w-0">
          <div className="flex items-center justify-between gap-8">
            <span className="flex items-center gap-6 type-label-sm font-medium">
              <UserRound size={13} aria-hidden /> Patient brief
            </span>
            <button
              type="button"
              onClick={() => openChart('overview')}
              className="shrink-0 type-label-xs text-accent hover:underline focus-visible:outline-none focus-visible:shadow-focus"
            >
              Quick view chart
            </button>
          </div>
          <p className="type-body-sm font-medium">
            {profile.display_name || 'Unknown patient'}
          </p>
          <SubscriptionStatusSummary subscription={data.subscription} />
          {identity.length > 0 && (
            <p className="type-body-sm text-ink-muted">
              {identity.join(' · ')}
            </p>
          )}
          {data.synthetic && (
            <Badge tone="info" size="sm" className="self-start">
              <FlaskConical size={10} aria-hidden /> Fictional test record
            </Badge>
          )}
          {(profile.email || profile.phone) && (
            <p className="text-11 text-ink-muted break-words">
              {[profile.email, profile.phone].filter(Boolean).join(' · ')}
            </p>
          )}
          {profile.id && <AIResponseControl patientId={String(profile.id)} />}
        </section>

        <SummaryCard conversationId={conversationId} />

        <section className="p-12 flex flex-col gap-6 min-w-0">
          <span className="type-label-sm font-medium">Consults</span>
          {appointments.slice(0, 3).map((appointment) => (
            <p key={appointment.id} className="type-body-sm">
              {appointment.appointment_type || 'Appointment'} ·{' '}
              {new Date(appointment.starts_at).toLocaleString([], {
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
              })}
              {appointment.clinician_name
                ? ` · ${appointment.clinician_name}`
                : ''}
              {appointment.status ? ` · ${appointment.status}` : ''}
            </p>
          ))}
          {appointments.length === 0 && (
            <p className="type-body-sm text-ink-muted">No consult found.</p>
          )}
        </section>

        <PatientFilesCard
          key={conversationId}
          conversationId={conversationId}
          synthetic={synthetic}
        />

        <section className="p-12 flex flex-col gap-8 min-w-0">
          <span className="flex items-center gap-6 type-label-sm font-medium">
            <Pill size={13} aria-hidden /> Active medications
          </span>
          <p className="text-11 text-ink-muted">
            Current medication records on the Healthie chart. Order directions
            below show the pharmacy prescription history.
          </p>
          {medications.length === 0 ? (
            <p className="type-body-sm text-ink-muted">
              None found in the connected record.
            </p>
          ) : (
            <div className="flex flex-col gap-8">
              {medications.map((medication) => (
                <div key={medication.id} className="flex flex-col gap-2">
                  <p className="type-body-sm font-medium">{medication.name}</p>
                  <p className="type-body-sm text-ink-muted">
                    {[medication.dose, medication.frequency, medication.route]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                  {medication.instructions && (
                    <p className="type-body-sm">{medication.instructions}</p>
                  )}
                </div>
              ))}
            </div>
          )}
          <SubscriptionPlanList subscription={data.subscription} />
        </section>

        {orders.length > 0 && (
          <section className="p-12 flex flex-col gap-8 min-w-0 max-h-[24rem] overflow-y-auto">
            <span className="type-label-sm font-medium">Order directions</span>
            <div className="flex flex-col divide-y divide-line-subtle">
              {orders.map((o, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <div
                  key={i}
                  className="py-8 first:pt-0 last:pb-0 flex flex-col gap-3"
                >
                  <p className="type-body-sm font-medium">{o.product}</p>
                  {o.directions && (
                    <p className="type-body-sm text-ink leading-relaxed">
                      {o.directions}
                    </p>
                  )}
                  <p className="text-11 text-ink-muted">
                    {o.date}
                    {o.status && (
                      <span className={STATUS_TONE[o.status] ?? ''}>
                        {' '}
                        · {o.status}
                      </span>
                    )}
                    {' · '}
                    <OrderPharmacyLabel order={o} />
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="p-12 flex flex-col gap-10 min-w-0 max-h-[28rem] overflow-y-auto">
          <div className="flex items-center justify-between gap-8">
            <span className="flex items-center gap-6 type-label-sm font-medium">
              <FileText size={13} aria-hidden /> Product-linked intake
            </span>
            {intakeForms.length > 0 && (
              <button
                type="button"
                onClick={() => openChart('intakes')}
                className="shrink-0 type-label-xs text-accent hover:underline focus-visible:outline-none focus-visible:shadow-focus"
              >
                All intakes ({intakeForms.length})
              </button>
            )}
          </div>
          {evidence.length === 0 ? (
            <p className="type-body-sm text-ink-muted">
              No safety or treatment highlights found
              {intakeForms.length > 0 ? '; review the full intake.' : '.'}
            </p>
          ) : (
            <div className="grid gap-8">
              {evidence.map((item, index) => (
                <div
                  // The same answer can appear in repeat product intakes.
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${item.form_name}-${item.question}-${index}`}
                  className="border border-line-subtle bg-sunken rounded p-9 flex flex-col gap-4 min-w-0"
                >
                  <div className="flex items-start gap-6">
                    <p className="type-label-xs text-ink-muted flex-1">
                      {item.question}
                    </p>
                    <Badge
                      tone={item.importance === 'safety' ? 'danger' : 'warning'}
                      size="sm"
                    >
                      {item.importance}
                    </Badge>
                  </div>
                  <p className="type-body-sm font-medium break-words">
                    {item.answer}
                  </p>
                  <p className="text-11 text-ink-muted truncate">
                    {item.form_name}
                    {item.submitted_at && (
                      <> · {new Date(item.submitted_at).toLocaleDateString()}</>
                    )}
                  </p>
                </div>
              ))}
            </div>
          )}
          {productIntakes.length > 0 && (
            <p className="text-11 text-ink-muted">
              {productIntakes.length} mapped intake submission
              {productIntakes.length === 1 ? '' : 's'} shown first for the
              active treatment. Open All intakes for every historical form.
            </p>
          )}
        </section>

        <p className="p-12 text-11 text-ink-muted">
          AI summaries and drafts use this same evidence. Verify against the
          source record before sending.
        </p>
      </div>
      <PatientChartDrawer
        open={chartOpen}
        onClose={() => setChartOpen(false)}
        data={data}
        view={chartView}
        onViewChange={setChartView}
      />
    </>
  );
}

export type ChartView = 'overview' | 'intakes' | 'checkins';

type IntakeGroup = {
  key: string;
  name: string;
  submissions: IntakeFormPreview[];
};

function groupIntakeForms(forms: IntakeFormPreview[]): IntakeGroup[] {
  const groups = new Map<string, IntakeGroup>();
  for (const form of forms) {
    const key = form.form_key || form.form_slug || form.form_name;
    const group = groups.get(key);
    if (group) {
      group.submissions.push(form);
    } else {
      groups.set(key, {
        key,
        name: form.form_name,
        submissions: [form],
      });
    }
  }
  return Array.from(groups.values());
}

function ChartCheckins({
  patientId,
  onShowIntakes,
}: {
  patientId: string | undefined;
  onShowIntakes: () => void;
}) {
  const history = useClinicianCheckinDoseHistory(patientId ?? null);
  return (
    <div className="flex flex-col gap-12">
      {/* Open requests with their actions, next to the conversation that raised
          them. Loaded independently, so a history failure doesn't hide them. */}
      {patientId && <DoseChangeHolds patientId={patientId} openOnly />}
      {history.isLoading ? (
        <p className="type-body-sm text-ink-muted py-10">Loading check-ins…</p>
      ) : history.isError || !history.data ? (
        <p className="type-body-sm text-danger py-10">
          Check-ins and dose changes could not be loaded.
        </p>
      ) : (
        <CheckinDoseHistoryView
          data={history.data}
          openCheckin={
            <button
              type="button"
              onClick={onShowIntakes}
              className="type-label-xs underline self-start"
            >
              View full check-in
            </button>
          }
        />
      )}
    </div>
  );
}

export function PatientChartDrawer({
  open,
  onClose,
  data,
  view,
  onViewChange,
}: {
  open: boolean;
  onClose: () => void;
  data: PatientBriefResponse;
  view: ChartView;
  onViewChange: (view: ChartView) => void;
}) {
  const { profile } = data;
  const forms = data.intake_forms ?? [];
  const groups = groupIntakeForms(forms);
  const [selectedId, setSelectedId] = useState(forms[0]?.id ?? '');
  const selected = forms.find((form) => form.id === selectedId) ?? forms[0];
  const selectedGroup = groups.find((group) =>
    group.submissions.some((form) => form.id === selected?.id),
  );
  const selectedIndex = selectedGroup
    ? selectedGroup.submissions.findIndex((form) => form.id === selected?.id)
    : -1;

  useEffect(() => {
    if (!forms.some((form) => form.id === selectedId)) {
      setSelectedId(forms[0]?.id ?? '');
    }
  }, [forms, selectedId]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="wide"
      title={`Patient chart · ${profile.display_name || 'Patient'}`}
    >
      <div className="p-15 md:p-20 flex flex-col gap-15">
        <div className="flex flex-col gap-4 border-b border-line-subtle pb-15">
          <h2 className="type-heading-sm">Chart quick view</h2>
          <p className="type-body-sm text-ink-muted">
            Patient identity, clinical medications, treatment directions, and
            complete intake history—without leaving this conversation.
          </p>
        </div>

        {profile.id && <CareTeamNotes patientId={profile.id} />}

        <div className="flex border-b border-line-subtle" role="tablist">
          <ChartTab
            active={view === 'overview'}
            onClick={() => onViewChange('overview')}
          >
            Overview
          </ChartTab>
          <ChartTab
            active={view === 'intakes'}
            onClick={() => onViewChange('intakes')}
          >
            Intakes ({forms.length})
          </ChartTab>
          <ChartTab
            active={view === 'checkins'}
            onClick={() => onViewChange('checkins')}
          >
            Check-ins &amp; dose changes
          </ChartTab>
        </div>

        {view === 'checkins' ? (
          <ChartCheckins
            patientId={profile.id}
            onShowIntakes={() => onViewChange('intakes')}
          />
        ) : view === 'overview' ? (
          <ChartOverview
            data={data}
            onShowIntakes={() => onViewChange('intakes')}
          />
        ) : forms.length === 0 ? (
          <p className="type-body-sm text-ink-muted py-10">
            No submitted intakes are available for this patient.
          </p>
        ) : (
          <div className="grid md:grid-cols-[15rem_minmax(0,1fr)] gap-15 items-start">
            <nav
              aria-label="Intake history"
              className="border border-line-subtle flex flex-col max-h-[70vh] overflow-y-auto"
            >
              {groups.map((group) => (
                <div
                  key={group.key}
                  className="border-b last:border-b-0 border-line-subtle"
                >
                  <div className="p-9 bg-sunken flex items-start justify-between gap-6">
                    <span className="type-label-xs font-medium">
                      {group.name}
                    </span>
                    <span className="text-11 text-ink-muted tabular-nums">
                      {group.submissions.length}
                    </span>
                  </div>
                  {group.submissions.map((form, index) => {
                    const active = form.id === selected?.id;
                    return (
                      <button
                        key={form.id}
                        type="button"
                        onClick={() => setSelectedId(form.id)}
                        aria-current={active ? 'true' : undefined}
                        className={`w-full text-left px-10 py-8 border-t border-line-subtle flex flex-col gap-2 hover:bg-sunken focus-visible:outline-none focus-visible:shadow-focus ${active ? 'bg-accent-wash' : ''}`}
                      >
                        <span className="type-body-sm font-medium">
                          {form.submitted_at
                            ? new Date(form.submitted_at).toLocaleDateString()
                            : 'Date unavailable'}
                          {index === 0 && group.submissions.length > 1
                            ? ' · Latest'
                            : ''}
                        </span>
                        <span className="text-11 text-ink-muted">
                          {form.source} · {form.answers.length} answers
                        </span>
                      </button>
                    );
                  })}
                </div>
              ))}
            </nav>

            {selected && (
              <section className="min-w-0 flex flex-col gap-10">
                <div className="flex flex-col gap-3">
                  <h3 className="type-heading-sm">{selected.form_name}</h3>
                  <p className="type-label-xs text-ink-muted">
                    {selected.source}
                    {selectedGroup && selectedGroup.submissions.length > 1 && (
                      <>
                        {' '}
                        · Submission {selectedIndex + 1} of{' '}
                        {selectedGroup.submissions.length}
                      </>
                    )}
                    {selected.submitted_at && (
                      <>
                        {' '}
                        · Submitted{' '}
                        {new Date(selected.submitted_at).toLocaleString()}
                      </>
                    )}
                  </p>
                </div>
                <dl className="border border-line-subtle divide-y divide-line-subtle">
                  {selected.answers.map((answer, index) => (
                    <div
                      // Some forms can legitimately repeat the same prompt.
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${answer.question}-${index}`}
                      className="p-10 grid gap-4"
                    >
                      <dt className="flex items-start gap-6 type-label-xs text-ink-muted">
                        <span className="flex-1">{answer.question}</span>
                        {answer.importance !== 'context' && (
                          <Badge
                            tone={
                              answer.importance === 'safety'
                                ? 'danger'
                                : 'warning'
                            }
                            size="sm"
                          >
                            {answer.importance}
                          </Badge>
                        )}
                      </dt>
                      <dd className="type-body-sm whitespace-pre-wrap break-words">
                        {answer.answer}
                      </dd>
                    </div>
                  ))}
                </dl>
              </section>
            )}
          </div>
        )}
      </div>
    </Drawer>
  );
}

function ChartTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`px-12 py-8 type-label-sm border-b-2 -mb-px focus-visible:outline-none focus-visible:shadow-focus ${active ? 'border-ink text-ink' : 'border-transparent text-ink-muted hover:text-ink'}`}
    >
      {children}
    </button>
  );
}

function ChartOverview({
  data,
  onShowIntakes,
}: {
  data: PatientBriefResponse;
  onShowIntakes: () => void;
}) {
  const { profile, medications, orders, intake_forms: forms } = data;
  const address = profile.address
    ? [
        profile.address.line1,
        profile.address.line2,
        [
          profile.address.city,
          profile.address.state,
          profile.address.postal_code,
        ]
          .filter(Boolean)
          .join(', '),
      ]
        .filter(Boolean)
        .join(' · ')
    : null;
  const subscription = data.subscription
    ? subscriptionSummary(data.subscription)
    : null;
  const fields = [
    ['Preferred name', profile.display_name],
    [
      'Subscription',
      subscription
        ? [subscription.label, subscription.detail].filter(Boolean).join(' · ')
        : null,
    ],
    ['Legal first name', profile.legal_name],
    [
      'Date of birth',
      profile.date_of_birth
        ? `${new Date(`${profile.date_of_birth}T00:00:00`).toLocaleDateString()}${profile.age ? ` · ${profile.age} years` : ''}`
        : null,
    ],
    ['Sex', profile.sex],
    ['Email', profile.email],
    ['Phone', profile.phone],
    ['Address', address],
    ['Timezone', profile.timezone],
  ].filter((field): field is [string, string] => Boolean(field[1]));

  return (
    <div className="flex flex-col gap-15">
      <section className="flex flex-col gap-8">
        <h3 className="type-label-sm font-medium">Patient information</h3>
        <dl className="border border-line-subtle divide-y divide-line-subtle">
          {fields.map(([label, value]) => (
            <div
              key={label}
              className="p-10 grid sm:grid-cols-[9rem_minmax(0,1fr)] gap-3 sm:gap-10"
            >
              <dt className="type-label-xs text-ink-muted">{label}</dt>
              <dd className="type-body-sm break-words">{value}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="flex flex-col gap-8">
        <h3 className="flex items-center gap-6 type-label-sm font-medium">
          <Pill size={13} aria-hidden /> Active clinical medications
        </h3>
        {medications.length === 0 ? (
          <p className="type-body-sm text-ink-muted">
            No active medications found.
          </p>
        ) : (
          <div className="border border-line-subtle divide-y divide-line-subtle">
            {medications.map((medication) => (
              <div key={medication.id} className="p-10 flex flex-col gap-3">
                <p className="type-body-sm font-medium">{medication.name}</p>
                <p className="type-body-sm text-ink-muted">
                  {[medication.dose, medication.frequency, medication.route]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
                {medication.instructions && (
                  <p className="type-body-sm">{medication.instructions}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="flex flex-col gap-8">
        <h3 className="type-label-sm font-medium">Treatment directions</h3>
        {orders.length === 0 ? (
          <p className="type-body-sm text-ink-muted">
            No treatment orders found.
          </p>
        ) : (
          <div className="border border-line-subtle divide-y divide-line-subtle">
            {orders.map((order, index) => (
              <div
                // Order rows currently have no stable id in this API shape.
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                className="p-10 flex flex-col gap-3"
              >
                <p className="type-body-sm font-medium">{order.product}</p>
                {order.medication && (
                  <p className="type-body-sm">{order.medication}</p>
                )}
                {order.directions && (
                  <p className="type-body-sm text-ink-muted">
                    {order.directions}
                  </p>
                )}
                <p className="text-11 text-ink-muted">
                  {[order.date, order.status].filter(Boolean).join(' · ')}
                  {' · '}
                  <OrderPharmacyLabel order={order} />
                </p>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="border border-line-subtle p-10 flex items-center justify-between gap-10">
        <div className="flex flex-col gap-2">
          <h3 className="type-label-sm font-medium">Intake history</h3>
          <p className="type-body-sm text-ink-muted">
            {forms.length} submission{forms.length === 1 ? '' : 's'} across{' '}
            {groupIntakeForms(forms).length} intake type
            {groupIntakeForms(forms).length === 1 ? '' : 's'}
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={onShowIntakes}
        >
          Review
        </Button>
      </section>

      <p className="text-11 text-ink-muted">
        This quick view combines the connected clinical chart with System Labs
        treatment and intake records. Intake aliases are combined and
        authentication fields are omitted.
      </p>
    </div>
  );
}
